import "@babel/polyfill";
import preact from 'preact';
import isFunction from 'lodash-es/isFunction';
import { initializeStore } from './store/AttributesStore';
import manager from './store/Attributes.manager';
import Fab from './components/Fab';
import Frame from './components/Frame';
import { licenseId, locationsLimit, protocol, widgetHost } from './config';
import { CustomStyle } from './services/style/CustomStyle';
import { getBrowserInfo, getReferrer, getResourceUrl, polyfill } from './services/utils';
import locationService from './services/location/location.service';
import { createSimpleLocation, createExtendedLocation } from './services/location/location.utils.service';
import { createUfNS } from './services/api/ufNamespace';
import { playSound, setSound } from './services/sound.service'

polyfill();

const { Component, h, render } = preact;

let root = null; // rendered container node

class Container extends Component {
  componentDidMount() {
    //https://reactjs.org/docs/refs-and-the-dom.html
    //https://medium.com/@ebakhtarov/handling-of-iframes-in-react-f038be46ac24
    const frame = this.base.getElementsByTagName('iframe')[0];

    // api - functions communicating with widget through postMessage
    const api = manager.createApi(frame);

    manager.setLanguage(document.documentElement.lang);

    // expose new $uf namespace
    window.$uf = createNamespace(window, api);

    window.addEventListener('message', onWindowMessage, false);
  }

  render() {
    return h('div', { class: ' da0156dfc57b9ef01669068861c5cb38 ' },
      h(Frame, { location: getWidgetLocation().href }),
      h(Fab)
    );
  }
}

// kick everything off
const AttributesStore = initializeStore();
// hide everything from view until widget initialization
CustomStyle.initialize();

root = render(h(Container), document.body);

function onWindowMessage(event) {
  let origin = event.origin,
    method = event.data.method;

  if (origin !== getWidgetLocation().origin) {
    return;
  }

  switch (method) {
    case 'SetGeneralData':
      return onSetGeneralData(event.data);
    case 'Initialize':
      return onInitialize(event.data);
    case 'Minimize':
      return hide();
    case 'showWidgetFrame':
      return show();
    case 'PlaySound':
      return onPlaySound(getWidgetLocation(), event.data.params.url);
    case 'HideLoader':
      return onHideLoaderChange(event.data);
    case 'Notify':
      return onNotify(event.data);
    case 'GetGeneralInfo':
      return onGetGeneralInfo(event.data);
    case 'onChatStart':
      return manager.onChatStart();
    case 'onChatEnd':
      return manager.onChatEnd();
  }
}

function onPlaySound(widgetLocation, url) {
  return playSound(getResourceUrl(widgetLocation, url));
}

function onSetSound(widgetLocation, url) {
  return setSound(getResourceUrl(widgetLocation, url));
}

function onInitialize(data) {
  const params = data.params || {};

  CustomStyle.activate();
  locationService.activate(licenseId, +locationsLimit);
  onSetSound(getWidgetLocation(), params.notificationSoundUrl);

  manager.setFlattenedTranslation(params.translations);

  window.$uf._.forEach(cb => {
    isFunction(cb) && cb(params);
  });

  if (params.mediaMobileWidth) {
    manager.setMediaMobileWidth(params.mediaMobileWidth);
  }

  // isFrameOpened is set earlier by setGeneralData
  AttributesStore.get('isFrameOpened') ? show() : hide();
  manager.sendResponseResult(data.id);
}


// interface GeneralData {
//     channels: Channels;
//     language?: string;
//     logo: string;
//
//     [propName: string]: any;
// }

// interface SetGeneralDataEvent extends JsonRpcEvent {
//     params: GeneralData;
// }

/**
 * @param {SetGeneralDataEvent} data
 */
function onSetGeneralData(data) {
  var params = data.params || {};

  // return manager.setGeneralData(params);

  Object.keys(params).forEach(function (key) {
    switch (key) {
      case 'actions':
        return manager.setActions(params[key]);
      case 'canUseAPI':
        return manager.setCanUseAPI(params[key]);
      case 'channels':
        return manager.setChannels(params[key]);
      case 'language':
        return manager.setLanguage(params[key]);
      case 'flashcard':
        return manager.setFlashcard(params[key]);
      case 'hideAllBtn':
        return manager.setHideAllBtn(params[key]);
      case 'isFrameOpened':
        return manager.setFrameVisivility(params[key]);
      case 'styleTemplate':
        return manager.setStyleTemplate(params[key]);
      case 'widgetVersion':
        return manager.setWidgetVersion(params[key]);
      case 'logo':
        return manager.setLogo(params[key]);
      case 'color':
        return manager.setColor(params[key]);
      case 'offsetX':
        return manager.setOffsetX(params[key]);
      case 'offsetY':
        return manager.setOffsetY(params[key]);
      case 'position':
        return manager.setPosition(params[key]);
      case 'styleTemplate':
        return manager.setStyleTemplate(params[key]);
      case 'isFrameOpened':
        return manager.setFrameVisivility(params[key]);
      default:
        console.error('unknown request received from widget:', key);
    }
  });
}

// local API start
function show(channel) {
  manager.setFrameVisivility(true);
  manager.goToChannel(channel);
}

function hide() {
  manager.setFrameVisivility(false);
}

function toggle() {
  manager.setFrameVisivility(!AttributesStore.get('isFrameOpened'));
}

function remount() {
  //todo reactivate all services

  if (!root) {
    return;
  }

  // https://github.com/developit/preact/issues/53

  // destroy(unmount) container
  render('', document.body, root);
  // mount container - reloads widget
  root = render(h(Container), document.body);
}

// local API end

/**
 * @param {JsonRpcRequest} data
 */
function onHideLoaderChange(data) {
  var isLoaderHidden = data.params;

  // hide fab component
  manager.hideLoader(isLoaderHidden);
}

/**
 * @param {JsonRpcRequest} data
 */
function onNotify(data) {
  manager.onNotify(data.params.length);
}

function onGetGeneralInfo(data) {
  var params = data.params || {};
  // data.params.referrer - referrer from widget localStorage
  manager.sendResponseResult(data.id, getGeneralInfo(params.referrer));
}

function getGeneralInfo(widgetReferrer) {
  const locationObject = createExtendedLocation(window.location, document.title);

  return {
    browserInfo: getBrowserInfo(),
    generalData: window.$uf && window.$uf.params && window.$uf.params.generalData,
    language: AttributesStore.get('language'),
    loaderVersion: AttributesStore.get('loaderVersion'),
    location: locationObject.href,
    referrer: getReferrer(locationObject.hostname, document.referrer, widgetReferrer)
  };
}

/**
 * joins config params, replace double slashes and return widget location
 *
 * @returns {ILocation}
 */
function getWidgetLocation() {
  const location = `${protocol}//` + [widgetHost, licenseId]
    .join('/').replace(/\/+/g, '/');
  return createSimpleLocation(location);
}

function createNamespace(global, api) {
  const getVersion = function () { // api.version
    return {
      loader: AttributesStore.get('loaderVersion'),
      widget: AttributesStore.get('widgetVersion')
    };
  };

  const localApi = {
    show, hide, toggle, remount, getVersion
  };

  return createUfNS(global, api, localApi);
}
